
import { useEffect, useRef, useState } from "react";
import { useService } from "@/context/ServiceContext";
import { useLocation, useNavigate } from "react-router-dom";

interface ServiceLayoutProps {
  serviceId: "nutrition-counselling" | "gut-health" | "weight-management" | "sports-nutrition" | "chronic-diseases" | "lifecycle-nutrition";
  children: React.ReactNode;
}

export const ServiceLayout = ({ serviceId, children }: ServiceLayoutProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { activeService, setActiveService } = useService();
  const location = useLocation();
  const navigate = useNavigate();
  const hasScrolledRef = useRef(false);
  const [isVisible, setIsVisible] = useState(false);

  // Set up intersection observer with more reliable detection settings
  useEffect(() => {
    if (!ref.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        const isIntersecting = entry.isIntersecting;
        
        setIsVisible(isIntersecting);
        
        if (isIntersecting) {
          console.log(`Service is now visible: ${serviceId}`);
          setActiveService(serviceId);
          
          // Only update URL if we're on the services page and not already at this URL
          if ((location.pathname === "/services" || location.pathname.startsWith("/services/")) && 
              !location.pathname.endsWith(serviceId)) {
            console.log(`Updating URL to /services/${serviceId}`);
            navigate(`/services/${serviceId}`, { replace: true });
          }
        }
      },
      {
        root: null,
        rootMargin: "-40% 0px -40% 0px", // Consider visible when more in the center of the viewport
        threshold: 0.1, // Trigger when at least 10% is visible
      }
    );

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [serviceId, setActiveService, navigate, location.pathname]);

  // Handle direct URL access with improved reliability
  useEffect(() => {
    // Only run this once per service
    if (hasScrolledRef.current) return;
    
    // If the URL matches this service, scroll to it on first render
    if (location.pathname === `/services/${serviceId}` && ref.current && !hasScrolledRef.current) {
      console.log(`URL matches service, will scroll to: ${serviceId}`);
      
      // Use a somewhat longer delay for better reliability
      setTimeout(() => {
        if (ref.current) {
          ref.current.scrollIntoView({ 
            behavior: "smooth",
            block: "start"
          });
          hasScrolledRef.current = true;
        }
      }, 700);
    }
  }, [serviceId, location.pathname]);

  return (
    <div 
      id={serviceId} 
      ref={ref} 
      className="scroll-mt-24"
      role="tabpanel"
      aria-labelledby={`tab-${serviceId}`}
      tabIndex={0}
      data-active={isVisible}
    >
      {children}
    </div>
  );
};
